import { useMemo, useState } from "preact/hooks";
import { Suspense, lazy } from "preact/compat";
import { useNavigate } from "react-router";
import { Text, Localizer, useText } from "preact-i18n";
import clsx from "clsx";
import { useQuery, useApolloClient } from "@apollo/client";

import {
  ButtonBase,
  IconButton,
  Toolbar,
  Tooltip,
  LinearProgress,
  MenuItem,
  Divider,
  Menu,
  ListItemText,
} from "@mui/material";

import { Help, Menu as MenuIcon, Settings } from "@mui/icons-material";

import { useLoading } from "../../lib/apollo/client";
import { useUserInitial } from "../../lib/useUserInfo";

import NewObfuscatedAddressButton from "../NewObfuscatedAddressButton/NewObfuscatedAddressButton";
import SearchBar from "../SearchBar/SearchBar";

import { MainAddressQuery } from "./Header.graphql";

import style from "./style.module.css";
import GreenDot from "../GreenDot/GreenDot";

const ConnectionPopover = lazy(() =>
  import("../ConnectionPopover/ConnectionPopover")
);

const OptionsMenu = () => {
  const navigate = useNavigate();
  const [anchorElement, setAnchorElement] = useState(null);

  const { data } = useQuery(MainAddressQuery, { fetchPolicy: "network-only" });

  const { userInitial } = useUserInitial();

  const client = useApolloClient();

  return (
    <span>
      <ButtonBase
        focusRipple
        aria-label="more"
        aria-controls="menu"
        aria-haspopup="true"
        size="small"
        className="flex items-center justify-center w-8 h-8"
        style={{
          borderRadius: "9999px",
        }}
        color="inherit"
        edge="end"
        onClick={(event) => setAnchorElement(event.currentTarget)}
      >
        <span class="rounded-full w-8 h-8 flex items-center justify-center border border-blue-400 bg-blue-400 text-white">
          <span>{userInitial}</span>
        </span>
      </ButtonBase>

      <Menu
        id="menu"
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        keepMounted
        className="mt-1"
        onClose={() => setAnchorElement(null)}
      >
        <Localizer>
          <Tooltip title={<Text id="Header.accountSettings" />}>
            <a
              href="https://wasc.io/console/settings"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div class="inline-flex px-4 pt-2 pb-3 items-center align-center">
                <span class="rounded-full w-6 h-6 flex items-center justify-center border border-blue-400 bg-blue-400 text-white">
                  <span>{userInitial}</span>
                </span>
                <span class="ml-2 underline">{data?.mainAddress?.address}</span>
                <div class="ml-1">
                  <Settings />
                </div>
              </div>
            </a>
          </Tooltip>
        </Localizer>
        <Divider />
        <a href="/dashboard/settings">
          <MenuItem onClick={() => setAnchorElement(null)}>
            <Text id="Header.settings" />
          </MenuItem>
        </a>
        <MenuItem
          onClick={() => {
            setAnchorElement(null);
            // TODO Revoke refresh Token at Wasc apis
            window.localStorage.removeItem("accessToken");
            window.localStorage.removeItem("tokenExpires");
            window.localStorage.removeItem("refreshToken");
            window.localStorage.removeItem("INBOX");

            client.clearStore();

            navigate("/");
          }}
        >
          <Text id="Header.logout" />
        </MenuItem>
      </Menu>
    </span>
  );
};

export const StatusDot = () => {
  const [anchorElement, setAnchorElement] = useState(null);

  return (
    <>
      <div
        class="h-full w-5 flex items-center justify-center "
        onMouseEnter={(event) => setAnchorElement(event.currentTarget)}
      >
        <GreenDot />
      </div>
      <Suspense>
        <ConnectionPopover
          anchorElement={anchorElement}
          onClose={() => setAnchorElement(null)}
        />
      </Suspense>
    </>
  );
};

const HelpMenu = () => {
  const [anchorElement, setAnchorElement] = useState(null);
  const {
    questionSubject,
    overflowingSubject,
    overflowingBody,
    notWorkingSubject,
    notWorkingBody,
  } = useText({
    questionSubject: "Header.question.subject",
    overflowingSubject: "Header.inboxOverflowing.subject",
    overflowingBody: "Header.inboxOverflowing.body",
    notWorkingSubject: "Header.notWorking.subject",
    notWorkingBody: "Header.notWorking.body",
  });

  return (
    <>
      <Localizer>
        <Tooltip title={<Text id="Header.helpMenu" />}>
          <IconButton
            focusRipple
            aria-haspopup="true"
            size="small"
            edge="end"
            onClick={(event) => setAnchorElement(event.currentTarget)}
          >
            <Help />
          </IconButton>
        </Tooltip>
      </Localizer>

      <Menu
        id="menu"
        dense
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        keepMounted
        className="mt-1"
        onClose={() => setAnchorElement(null)}
      >
        <MenuItem
          onClick={() => {
            window.open(
              `/popup/new-mail?recipients=support@wasc.io&subject=${questionSubject}`,
              null,
              "height=500,width=800,status=yes,toolbar=no,menubar=no,location=no"
            );
            setAnchorElement(null);
          }}
        >
          <Text id="Header.question.button" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            window.open(
              `/popup/new-mail?recipients=support@wasc.io&subject=${overflowingSubject}&body=${overflowingBody}`,
              null,
              "height=500,width=800,status=yes,toolbar=no,menubar=no,location=no"
            );
            setAnchorElement(null);
          }}
        >
          <Localizer>
            <ListItemText
              primary={<Text id="Header.inboxOverflowing.button" />}
              secondary={<Text id="Header.inboxOverflowing.sub" />}
            />
          </Localizer>
        </MenuItem>

        <MenuItem
          onClick={() => {
            window.open(
              `/popup/new-mail?recipients=support@wasc.io&subject=${notWorkingSubject}&body=${notWorkingBody}`,
              null,
              "height=500,width=800,status=yes,toolbar=no,menubar=no,location=no"
            );

            setAnchorElement(null);
          }}
        >
          <Text id="Header.notWorking.button" />
        </MenuItem>
      </Menu>
    </>
  );
};

const Header = ({ toggleDrawer }) => {
  const loading = useLoading();
  const inbox = useMemo(() => window.localStorage.getItem("INBOX"));

  return (
    <Toolbar className="w-full h-full shadow z-30 relative">
      <div
        class={clsx(
          style.header,
          "p-3 absolute top-0 left-0 w-full items-center"
        )}
      >
        <div>
          <div class="lg:block hidden" />
          <div className="lg:hidden flex justify-center">
            <Tooltip title="Mailboxes">
              <IconButton size="small" color="inherit" onClick={toggleDrawer}>
                <MenuIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div class="md:flex hidden items-center">
          <a
            class="self-center text-lg ml-2"
            href={`/dashboard${inbox ? `/${inbox}` : ""}`}
          >
            <h1 class="whitespace-nowrap">Wasc Mail</h1>
          </a>
        </div>
        <div class="md:hidden block" />

        <div class="h-full flex items-center justify-center mt-0.5">
          <StatusDot />
        </div>

        <div class="w-full lg:px-16 md:px-8 px-0">
          <SearchBar />
        </div>

        <div class="lg:block md:hidden hidden">
          <NewObfuscatedAddressButton />
        </div>
        <div class="lg:hidden md:block block" />

        <HelpMenu />

        <OptionsMenu />
      </div>
      {loading && (
        <LinearProgress
          // Need to overwrite the MUI style
          style="position: absolute"
          className="left-0 bottom-0 w-full"
          color="inherit"
        />
      )}
    </Toolbar>
  );
};

export default Header;
